import React, { useEffect, useState, useRef } from 'react'
import classNames from 'classnames'
import { Helmet } from 'react-helmet'
import { useLazyQuery, useApolloClient } from '@apollo/react-hooks'
import { useForm, Controller } from 'react-hook-form'
import { useLocation, Redirect, useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import jwtDecode from 'jwt-decode'
import ReactSVG from 'react-svg'
import {
  __,
  equals,
  find,
  gt,
  pathEq,
  pathOr,
  pipe,
  prop,
  where,
  whereEq
} from 'ramda'

import Input from '../forms/input'
import PasswordInput from '../forms/password-input'
import Button from '../button'
import Checkbox from '../forms/checkbox'
import NavLinkWithQuery from '../shared/nav-link-with-query'
import { getModalLocation, selectPremiumIntroductionModal } from '../../lib/modal'
import {
  MODALS, FORM_VALUES, FORM_MESSAGES, SUBSCRIPTION_STATUS, SIGN_UP_SCREENS, LOGIN_SCREENS
} from '../../constants'
import ACCOUNT_QUERY from '../../../graphql/queries/account.gql'
import LOGIN_QUERY from '../../../graphql/queries/login.gql'
import { navigationGa, loginGa } from '../../lib/analytics/ga'
import { pushQueryParams } from '../../lib/location'
import { getGQLErrorMsg } from '../../lib/apollo'
import { customDimensions } from '../../lib/analytics/custom-dimensions'
import { E2E_SELECTORS } from '../../e2e-selectors'
import { REGEX } from '../../utils/regex'
import { getSearchProperty } from '../../lib/query-string'
import { useConfig } from '../../hooks/use-config'
import { useIsAuthenticated } from '../../hooks/use-is-authenticated'
import { STORAGE_KEY_EMAIL_ON_GUEST_HOME_PAGE } from '../../storage-keys'
import { isNotNil } from '../../lib/fp'
import { segmentIdentify } from '../../segment/segment-identify'
import { segmentTrackSignin } from '../../segment/segment-track'
import { getOperatingSystem, getDevice } from '../../utils/os-device'
import {
  findPremiumIntroductionVersion
} from '../../lib/config'
import {
  enableLoadingOverlay,
  endLoadingOverlay,
  login,
  saveHasSeenRenewalPrompt,
  saveHasSeenRepairPrompt,
  saveIntroductionVersion
} from '../../actions'
import NeonLogo from '../../../images/neon/neon-logo-yellow.svg'
import styles from './login.css'

function Login() {
  const client = useApolloClient()
  const location = useLocation()
  const history = useHistory()
  const { appConfig } = useConfig()
  // Wait for config so the post-login flow can determine whether to show the premium introduction.
  const isConfigReady = Boolean(appConfig?.values)
  const isAuthenticated = useIsAuthenticated()
  const dispatch = useDispatch()
  const firstElementRef = useRef(null)
  const [overlayKey, setOverlayKey] = useState(null)
  const [rememberMe, setRememberMe] = useState(true)
  const [errorMessage, setErrorMessage] = useState(null)
  const accounts = useSelector(state => state.accounts)
  const [checkLoginInfo,
    { loading: checkLoginLoading, data: checkLoginData, error: checkLoginError }
  ] = useLazyQuery(LOGIN_QUERY)

  const {
    control,
    handleSubmit,
    getValues,
    formState: {
      errors, touchedFields, isSubmitting
    }
  } = useForm({
    defaultValues: {
      email: getSearchProperty('email') || sessionStorage.getItem(STORAGE_KEY_EMAIL_ON_GUEST_HOME_PAGE) || '',
      password: ''
    },
    mode: 'onChange'
  })

  // Focus on the email input
  useEffect(() => {
    let timer

    if (firstElementRef.current) {
      timer = setTimeout(() => {
        firstElementRef.current?.focus()
      }, 100)
    }

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [])

  useEffect(() => {
    if (!checkLoginLoading && checkLoginData) {
      // Extract account data from LOGIN_QUERY and write it to the Apollo cache
      // so it can be reused by ACCOUNT_QUERY without refetching
      client.writeQuery({
        query: ACCOUNT_QUERY,
        data: {
          account: checkLoginData.login
        }
      })
    }
  }, [checkLoginLoading, checkLoginData])

  useEffect(() => {
    if (!checkLoginLoading && checkLoginData && isConfigReady) {
      const loginData = checkLoginData.login
      const {
        subscription, session, profiles, selectedProfile
      } = loginData
      const jwtDecoded = jwtDecode(prop('token', session))
      const {
        accountId, sessionId
      } = jwtDecoded

      const seenIntroductionVersion = pathOr(
        0,
        [accountId, 'lastSeenPremiumIntroductionVersion'],
        accounts
      )
      const hasSeenRepairPrompt = pathEq(
        [accountId, 'hasSeenRepairPrompt'],
        true,
        accounts
      )
      const hasSeenRenewalPrompt = pathEq(
        [accountId, 'hasSeenRenewalPrompt'],
        true,
        accounts
      )
      const subscriptionIsCancelled = pathEq(
        ['status'],
        SUBSCRIPTION_STATUS.CANCELLED,
        subscription
      )
      const subscriptionIsSuspended = pathEq(
        ['renewalStatus'],
        SUBSCRIPTION_STATUS.SUSPENDED,
        subscription
      )

      const premiumIntroduction = findPremiumIntroductionVersion(appConfig)
      const shouldSeeNewIntro = where({
        version: gt(__, seenIntroductionVersion),
        enabled: equals(true)
      })(premiumIntroduction)

      const shouldSeeRenewalPrompt = subscriptionIsCancelled && !hasSeenRenewalPrompt
      const shouldSeeRepairPrompt = subscriptionIsSuspended && !hasSeenRepairPrompt
      const shouldSeeWelcomeBack = subscription?.status === SUBSCRIPTION_STATUS.NONE

      const isDefaultProfile = pipe(
        find(whereEq({ id: selectedProfile, isDefault: true })),
        isNotNil
      )(profiles)

      dispatch(login(loginData, rememberMe))

      loginGa({
        action: 'Manual login',
        label: accountId,
        [customDimensions.SessionId]: sessionId,
        [customDimensions.UserId]: accountId
      })

      // Add segment data identify for sign in
      const currentProfile = profiles.find(item => item.id === selectedProfile)
      segmentIdentify(loginData, currentProfile)

      // Add segment data analytics for sign in
      const { name: osName } = getOperatingSystem()
      const { model } = getDevice()
      // Web app version GITHUB_COMMIT_HASH will be replaced during build
      segmentTrackSignin({
        email: loginData.email,
        os: osName,
        platform: jwtDecoded.platform,
        model,
        deviceId: jwtDecoded.deviceId,
        appVersion: 'df8e4af1fd603a0a026e79dc9640d250a353228b',
        browser: jwtDecoded.browser
      })

      const isTVEasyLogin = location.pathname === LOGIN_SCREENS.TV_EASY_LOGIN.path
      /* Handle the TV Easy Login flow
      ** 1. Remove `modal=login` from the URL after authentication
      ** 2. Preserve all other existing query parameters (e.g. pairCode)
      ** 3. Keep the user on the TV Easy Login page after login
      */
      if (isTVEasyLogin) {
        const params = new URLSearchParams(location.search)
        params.delete('modal')
        history.push({
          pathname: LOGIN_SCREENS.TV_EASY_LOGIN.path,
          search: params.toString()
        })
        dispatch(endLoadingOverlay(overlayKey, 'login', { timeout: 2000 }))
        return
      }

      // Determine the landing location after login
      // Users coming from the signup page should be redirected to home.
      const isOnSignupRoute = location.pathname.includes('sign-up')
      const loginLandingLocation = isOnSignupRoute
        ? { ...location, pathname: '/' }
        : location

      // Handle welcome dialog logic
      // Only show the welcome dialog when the user is on the default profile
      if (
        isDefaultProfile && (
          shouldSeeRenewalPrompt ||
          shouldSeeNewIntro ||
          shouldSeeRepairPrompt ||
          shouldSeeWelcomeBack
        )
      ) {
        if (shouldSeeRepairPrompt) {
          dispatch(saveHasSeenRepairPrompt(accountId))
        } else if (shouldSeeRenewalPrompt) {
          dispatch(saveHasSeenRenewalPrompt(accountId))
        } else if (shouldSeeNewIntro) {
          dispatch(
            saveIntroductionVersion(
              accountId,
              premiumIntroduction.version
            )
          )
        }
        const premiumIntroModalParams = selectPremiumIntroductionModal(
          subscription
        )
        // Run history.push after the current synchronous work finishes
        Promise.resolve().then(() => {
          pushQueryParams(
            history,
            loginLandingLocation,
            premiumIntroModalParams
          )
        })
      }

      if (isOnSignupRoute) {
        history.push('/')
      }
      dispatch(endLoadingOverlay(overlayKey, 'login', { timeout: 2000 }))
    }
  }, [checkLoginLoading, checkLoginData, isConfigReady])

  useEffect(() => {
    if (checkLoginError) {
      setErrorMessage(getGQLErrorMsg(checkLoginError))
      dispatch(endLoadingOverlay(overlayKey, 'login', { error: checkLoginError }))
    }
  }, [checkLoginError])

  const onSubmit = async (values) => {
    setErrorMessage(null)
    sessionStorage.removeItem(STORAGE_KEY_EMAIL_ON_GUEST_HOME_PAGE)
    const timestamp = new Date().getTime()
    setOverlayKey(timestamp)
    dispatch(enableLoadingOverlay(timestamp, 'login'))

    try {
      checkLoginInfo({
        variables: {
          email: values.email,
          password: values.password
        },
        fetchPolicy: 'network-only'
      })
    } catch (error) {
      dispatch(endLoadingOverlay(timestamp, 'login', { error }))
    }
  }

  const REGES_EMAIL = new RegExp(REGEX.email)
  const urlSafeEmail = encodeURI(getValues('email') || '')
  const passwordForgottenLocation = getModalLocation(
    location,
    MODALS.qsParams.passwordForgotten,
    { email: urlSafeEmail }
  )

  const onRememberMeChange = (event, value, checked) => {
    setRememberMe(checked)
  }

  if (isAuthenticated) {
    return <Redirect to={location.pathname} />
  }

  return (
    <div className={styles.wrapper}>
      <Helmet>
        <title>Login - NEON</title>
      </Helmet>

      <div className={styles.formWrapper}>
        <form
          className={classNames(styles.form, {
            [styles.isSubmitting]: isSubmitting
          })}
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className={styles.header}>
            <h1
              className={styles.title}
              data-lbx-e2e={E2E_SELECTORS.LOGIN_MODAL_HEADER}
            >
              Log in to Neon
            </h1>
          </div>

          <div
            className={styles.warning}
            data-lbx-e2e={E2E_SELECTORS.LOGIN_ERROR_TOP}
          >
            {errorMessage}
          </div>

          <div className={styles.formRow}>
            <div className={styles.formRowItem}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: FORM_MESSAGES.required,
                  validate: value => REGES_EMAIL.test(value) || FORM_MESSAGES.email.valid
                }}
                render={({ field }) => (
                  <Input
                    label="Email address"
                    type="email"
                    name={field.name}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e)
                      if (errorMessage) {
                        setErrorMessage(null)
                      }
                    }}
                    onBlur={field.onBlur}
                    warning={errors?.email?.message}
                    isValid={
                      !errorMessage &&
                      Boolean(
                        field.value && touchedFields?.email && !errors.email
                      )
                    }
                    labelClass={styles.label}
                    inputClass={styles.input}
                    setRef={firstElementRef}
                    data-e2e="login-email"
                  />
                )}
              />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formRowItem}>
              <Controller
                name="password"
                control={control}
                rules={{
                  required: FORM_MESSAGES.required,
                  minLength: {
                    value: FORM_VALUES.password.min,
                    message: FORM_MESSAGES.password.min
                  }
                }}
                render={({ field }) => (
                  <PasswordInput
                    label="Password"
                    type="password"
                    name={field.name}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e)
                      if (errorMessage) {
                        setErrorMessage(null)
                      }
                    }}
                    onBlur={field.onBlur}
                    warning={errors?.password?.message}
                    isValid={
                      !errorMessage &&
                      Boolean(
                        field.value && touchedFields?.password && !errors.password
                      )
                    }
                    labelClass={styles.label}
                    inputClass={styles.input}
                    data-e2e="login-password"
                    darkTheme
                  />
                )}
              />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formRowItem}>
              <Button
                disabled={isSubmitting}
                e2eIdentifier="login-submit-button"
                text="Log in"
              />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formRowItem}>
              <Checkbox
                label="Remember me"
                isChecked={rememberMe}
                onChange={onRememberMeChange}
                darkTheme
              />
              <span className={styles.modalLink}>
                Forgot your password?
                <NavLinkWithQuery
                  onClick={() => {
                    navigationGa({ action: 'Forgot password' })
                  }}
                  to={passwordForgottenLocation}
                  data-lbx-e2e={E2E_SELECTORS.RESET_PASSWORD_LINK}
                >
                  Reset your password here
                </NavLinkWithQuery>
              </span>
              <span className={styles.modalLink}>
                Don’t have a Neon account?
                <NavLinkWithQuery
                  onClick={() => {
                    navigationGa({ action: 'Go to sign up page - via login' })
                  }}
                  to={{ pathname: SIGN_UP_SCREENS.SIGN_UP.path }}
                >
                  Sign up here
                </NavLinkWithQuery>
              </span>
            </div>
          </div>
        </form>

        <div className={styles.imageWrapper}>
          <ReactSVG
            src={NeonLogo}
            className={styles.loginImage}
          />
        </div>
      </div>
    </div>
  )
}

export default Login
